/**
 * R3 (2026-09-17 owner ruling — no new worn art; the sheet says where gear COMES FROM instead):
 * every one of the 6 functional equipment slots is produced at one of the game's two crafting
 * benches — `weapon`/`offhand`/`head`/`body` gear is Forged at the Anvil (Cinderport, the plate
 * set) or made at the Hearth (Hearthollow, the robe set); `trinket`/`charm` gear is Carved at the
 * Hearth only. THE HEARTH VERB IS PER SLOT: the robe set's `offhand`/`head`/`body` recipes are
 * `Sew …`, but every robe `weapon` recipe is `Wrap … Stave` — the final review wave (2026-09-17)
 * caught the weapon line saying "Sewn". This module is the one place that mapping lives, so
 * `EquipmentSheet.tsx`'s empty-slot copy and its picker's empty-state links never drift from
 * `content/packs/core.v1/activities/*.json` or `recipes/*.json` — see `benchFor.test.ts`, which
 * reads those docs straight off disk the same way `speciesCatalog.contentDrift.test.ts` reads the
 * species pack, and checks each slot's Hearth verb against the first word of the Hearth recipes
 * whose output has that slot.
 */
export const BENCHES = {
  anvil: { code: 'cinderport_anvil', land: 'cinderport', name: 'the Anvil' },
  hearth: { code: 'hearthollow_hearth', land: 'hearthollow', name: 'the Hearth' },
} as const;

// weapon/offhand/head/body come from either bench; trinket/charm are hearth-only. The value is the
// past participle of the Hearth recipe verb for that slot (`Wrap` → "Wrapped", `Sew` → "Sewn").
export const HEARTH_VERB_FOR_SLOT: Readonly<Record<string, string>> = {
  weapon: 'Wrapped',
  offhand: 'Sewn',
  head: 'Sewn',
  body: 'Sewn',
};
const FORGES_AND_SEWS = new Set(Object.keys(HEARTH_VERB_FOR_SLOT));

function craftLinkFor(bench: (typeof BENCHES)[keyof typeof BENCHES]): { label: string; href: string } {
  return { label: `Go to ${bench.name} ›`, href: `/world/craft/${bench.code}?land=${bench.land}` };
}

/** One line of copy for an empty slot: where a piece for it is made. */
export function sourceLineFor(slot: string): string {
  const hearthVerb = HEARTH_VERB_FOR_SLOT[slot];
  return hearthVerb
    ? `Forged at ${BENCHES.anvil.name} · ${hearthVerb} at ${BENCHES.hearth.name}`
    : `Carved at ${BENCHES.hearth.name}`;
}

/** The bench(es) a slot's gear comes from, as ready-to-render `<Link>` targets. */
export function benchLinksFor(slot: string): { label: string; href: string }[] {
  const hearth = craftLinkFor(BENCHES.hearth);
  return FORGES_AND_SEWS.has(slot) ? [craftLinkFor(BENCHES.anvil), hearth] : [hearth];
}
